import { useEffect, useState } from "react";
import logoImage from "@assets/mrdhruv-logo-192.png";

interface StartupPageProps {
  onComplete: () => void;
}

export default function StartupPage({ onComplete }: StartupPageProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Show content after a brief delay
    const showTimer = setTimeout(() => {
      setShowContent(true);
    }, 200);

    // Start fade out after 2 seconds
    const fadeTimer = setTimeout(() => {
      setIsVisible(false);
    }, 2000);

    // Complete after fade out animation
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2500);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(fadeTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-[hsl(142,52%,12%)] transition-opacity duration-500 ${
        isVisible ? "opacity-100" : "opacity-0"
      }`}
    >
      {/* Cleaning machine geometric background pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(142,52%,18%)] via-[hsl(142,40%,16%)] to-[hsl(142,30%,12%)] startup-bg-pan"></div>
        <div className="absolute inset-0 opacity-70 startup-bg-pulse">
          <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,rgba(255,255,255,0.07)_0,rgba(255,255,255,0.07)_1px,transparent_1px,transparent_28px)]"></div>
          <div className="absolute inset-0 bg-[repeating-linear-gradient(0deg,rgba(255,255,255,0.05)_0,rgba(255,255,255,0.05)_1px,transparent_1px,transparent_28px)]"></div>
        </div>
        <div className="absolute inset-0 startup-bg-float">
          <div className="absolute -top-6 left-8 h-28 w-40 rounded-2xl border border-white/15 bg-white/5 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)] startup-shape-float"></div>
          <div className="absolute top-24 right-12 h-36 w-44 rounded-3xl border border-white/10 bg-white/5 startup-shape-drift startup-shape-delay-1"></div>
          <div className="absolute bottom-10 left-1/3 h-20 w-28 rounded-xl border border-white/10 bg-white/10 startup-shape-float startup-shape-delay-2"></div>
          <div className="absolute top-12 left-1/2 h-20 w-20 rounded-full border-2 border-white/20 bg-white/5 startup-shape-drift"></div>
          <div className="absolute top-12 left-1/2 h-10 w-10 translate-x-6 translate-y-6 rounded-full border border-white/30 bg-white/10 startup-shape-float startup-shape-delay-3"></div>
          <div className="absolute bottom-14 right-1/4 h-12 w-12 rounded-full border border-white/20 bg-white/10 startup-shape-float"></div>
          <div className="absolute bottom-14 right-1/4 h-4 w-4 translate-x-4 translate-y-4 rounded-full border border-white/30 bg-white/20 startup-shape-drift startup-shape-delay-2"></div>
          <div className="absolute top-1/3 left-16 h-2 w-10 rounded-full bg-white/20 startup-shape-float startup-shape-delay-1"></div>
          <div className="absolute top-[45%] left-16 h-2 w-16 rounded-full bg-white/15 startup-shape-drift startup-shape-delay-3"></div>
          <div className="absolute top-1/2 right-1/3 h-24 w-24 rounded-full border border-white/20 startup-shape-float"></div>
          <div className="absolute top-1/2 right-1/3 h-16 w-16 translate-x-4 translate-y-4 rounded-full border border-white/25 bg-white/5 startup-shape-drift startup-shape-delay-1"></div>
          <div className="absolute top-[56%] right-[36%] h-2 w-12 rounded-full bg-white/20 startup-shape-float startup-shape-delay-2"></div>
          <div className="absolute bottom-20 left-12 h-16 w-24 rounded-lg border border-white/15 bg-white/5 startup-shape-drift"></div>
          <div className="absolute bottom-16 left-16 h-8 w-8 rounded-full border border-white/20 bg-white/10 startup-shape-float startup-shape-delay-3"></div>
          <div className="absolute bottom-24 left-1/2 h-24 w-40 rounded-full border border-white/15 opacity-70 startup-shape-drift startup-shape-delay-2"></div>
          <div className="absolute top-8 right-8 h-20 w-32 rounded-[28px] border border-white/20 bg-white/10 startup-shape-float"></div>
          <div className="absolute top-12 right-24 h-6 w-10 rounded-full border border-white/25 bg-white/15 startup-shape-drift startup-shape-delay-1"></div>
          <div className="absolute top-20 right-6 h-2 w-24 rounded-full bg-white/20 startup-shape-float startup-shape-delay-2"></div>
          <div className="absolute top-22 right-4 h-3 w-10 rounded-full bg-white/25 startup-shape-drift startup-shape-delay-3"></div>
          <div className="absolute bottom-6 left-10 h-2 w-28 rotate-12 rounded-full bg-white/20 startup-shape-float startup-shape-delay-1"></div>
          <div className="absolute bottom-10 left-32 h-14 w-3 rotate-12 rounded-full bg-white/20 startup-shape-drift startup-shape-delay-2"></div>
          <div className="absolute bottom-4 left-6 h-5 w-16 rounded-full border border-white/20 bg-white/10 startup-shape-float startup-shape-delay-3"></div>
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.18)_100%)]"></div>
      </div>
      <div
        className={`relative z-10 flex flex-col items-center justify-center space-y-6 transition-all duration-700 ${
          showContent
            ? "opacity-100 scale-100 translate-y-0"
            : "opacity-0 scale-90 translate-y-4"
        }`}
      >
        <div className="flex items-center justify-center">
          <img
            src={logoImage}
            alt="MR DHRUV UNIVERSAL CLEANING LTD"
            className="h-14 md:h-16 w-auto"
            width={292}
            height={228}
            loading="eager"
            decoding="async"
          />
        </div>

        {/* Company Name */}
        <div className="text-center space-y-2">
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white tracking-wide">
            MR DHRUV
          </h1>
          <p className="text-white/80 text-sm md:text-base font-medium">
            Universal Cleaning Ltd
          </p>
        </div>

        {/* Loading Indicator */}
        <div className="flex space-x-2 mt-4" role="status" aria-live="polite" aria-label="Loading">
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce [animation-delay:-0.3s]" />
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce [animation-delay:-0.15s]" />
          <div className="w-2 h-2 bg-white/60 rounded-full animate-bounce" />
        </div>
      </div>
    </div>
  );
}
